import {
  Headline3,
  Label1,
  Label2,
  Icon,
} from "@bms-ai/bms-ai-ui-lib/components";
import type { StoryboardFrame } from "../../context/MultipleFileUploadContext";
import { useAppContext } from "../../context/app-context";
import { useState, useEffect, useMemo } from "react";
import { useCustomToast } from "../common/toast";
import "./StoryboardPreview.scss";

interface StoryboardPreviewProps {
  previewData: StoryboardFrame[] | null;
  onFrameSelectionChange?: (selectedFrames: StoryboardFrame[]) => void;
}

interface DimensionGroup {
  dimension: string;
  frames: StoryboardFrame[];
  isSelected: boolean;
}

const StoryboardPreview: React.FC<StoryboardPreviewProps> = ({
  previewData,
  onFrameSelectionChange,
}) => {
  const { selectedChannel } = useAppContext();
  const { showToast } = useCustomToast();
  const [selectedDimensions, setSelectedDimensions] = useState<Set<string>>(
    new Set(),
  );
  const [expandedDimensions, setExpandedDimensions] = useState<Set<string>>(
    new Set(),
  );
  const [totalSelectedFrames, setTotalSelectedFrames] = useState(0);
  const [frameSelectionsPerDimension, setFrameSelectionsPerDimension] =
    useState<Map<string, Set<number>>>(new Map());

  const isAnimatedBanner = selectedChannel?.label === "Animated Banner";

  const dimensionGroups = useMemo(() => {
    if (!previewData || previewData.length === 0) return [];

    const groupMap = new Map<string, StoryboardFrame[]>();
    previewData.forEach((frame) => {
      const dim = frame.dimension || "Unknown";
      if (!groupMap.has(dim)) {
        groupMap.set(dim, []);
      }
      groupMap.get(dim)!.push(frame);
    });

    // Convert to array and sort by dimension (ascending order)
    const groups: DimensionGroup[] = Array.from(groupMap.entries())
      .sort((a, b) => {
        // Extract numeric values from dimensions like "300x400"
        const getSize = (dim: string) => {
          const match = dim.match(/(\d+)/);
          return match ? parseInt(match[1]) : 0;
        };
        return getSize(a[0]) - getSize(b[0]);
      })
      .map(([dimension, frames]) => ({
        dimension,
        frames: frames.sort((a, b) => a.page - b.page),
        isSelected: false,
      }));

    return groups;
  }, [previewData]);

  // Default selection logic
  useEffect(() => {
    if (!isAnimatedBanner || dimensionGroups.length === 0) return;

    const totalFrames = previewData?.length || 0;
    const maxFrames = 20;

    if (totalFrames <= maxFrames) {
      // Select all dimensions if total frames <= 20
      const allDimensions = new Set(dimensionGroups.map((g) => g.dimension));
      setSelectedDimensions(allDimensions);
      setTotalSelectedFrames(totalFrames);
    } else {
      // First, try to select all <=20 frame groups up to 20 frames total
      let frameCount = 0;
      const selectedDims = new Set<string>();

      for (const group of dimensionGroups) {
        if (group.frames.length <= 20) {
          if (frameCount + group.frames.length <= maxFrames) {
            selectedDims.add(group.dimension);
            frameCount += group.frames.length;
          }
        }
      }

      // If no <=20 groups were selected, try selecting a 20+ group with first 20 frames
      if (selectedDims.size === 0) {
        const newFrameSelections = new Map<string, Set<number>>();
        for (const group of dimensionGroups) {
          if (group.frames.length > 20) {
            selectedDims.add(group.dimension);
            newFrameSelections.set(
              group.dimension,
              new Set(Array.from({ length: 20 }, (_, i) => i)),
            );
            frameCount = 20;
            break;
          }
        }
        setFrameSelectionsPerDimension(newFrameSelections);
      }

      setSelectedDimensions(selectedDims);
      setTotalSelectedFrames(frameCount);
    }
  }, [isAnimatedBanner, dimensionGroups, previewData]);

  // Notify parent when selection changes
  useEffect(() => {
    if (!isAnimatedBanner || !onFrameSelectionChange) return;

    const selectedFrames: StoryboardFrame[] = [];
    dimensionGroups.forEach((g) => {
      if (selectedDimensions.has(g.dimension)) {
        const frameIndices = frameSelectionsPerDimension.get(g.dimension);
        if (frameIndices && frameIndices.size > 0) {
          // For 20+ frame groups, include only selected frame indices
          frameIndices.forEach((idx) => {
            if (g.frames[idx]) {
              selectedFrames.push(g.frames[idx]);
            }
          });
        } else if (g.frames.length <= 20) {
          // For <=20 frame groups with no frame selections, include all frames
          selectedFrames.push(...g.frames);
        }
      }
    });

    onFrameSelectionChange(selectedFrames);
  }, [
    selectedDimensions,
    frameSelectionsPerDimension,
    isAnimatedBanner,
    onFrameSelectionChange,
    dimensionGroups,
  ]);

  const handleDimensionToggle = (dimension: string) => {
    const newSelected = new Set(selectedDimensions);
    const group = dimensionGroups.find((g) => g.dimension === dimension);
    if (!group) return;

    if (newSelected.has(dimension)) {
      // Unselect the dimension
      newSelected.delete(dimension);
      const frameIndices = frameSelectionsPerDimension.get(dimension);
      const framesInGroup = frameIndices
        ? frameIndices.size
        : group.frames.length;
      setTotalSelectedFrames((prev) => prev - framesInGroup);

      // Remove frame selections for this dimension
      const newFrameSelections = new Map(frameSelectionsPerDimension);
      newFrameSelections.delete(dimension);
      setFrameSelectionsPerDimension(newFrameSelections);

      // Check if only one 20+ frame group remains
      const remaining20PlusGroups = Array.from(newSelected).filter((dim) => {
        const g = dimensionGroups.find((dg) => dg.dimension === dim);
        return g && g.frames.length > 20;
      });

      if (remaining20PlusGroups.length === 1 && newSelected.size === 1) {
        // Auto-select first 20 frames from the remaining 20+ frame group if it has no selection yet
        const remainingDim = remaining20PlusGroups[0];
        if (!newFrameSelections.has(remainingDim)) {
          newFrameSelections.set(
            remainingDim,
            new Set(Array.from({ length: 20 }, (_, i) => i)),
          );
          setFrameSelectionsPerDimension(newFrameSelections);
          setTotalSelectedFrames(20);
        }
      }

      setSelectedDimensions(newSelected);
    } else {
      // Try to select the dimension
      // Check if any 20+ frame group is already selected
      const has20PlusGroupSelected = Array.from(newSelected).some((dim) => {
        const g = dimensionGroups.find((dg) => dg.dimension === dim);
        return g && g.frames.length > 20;
      });

      if (group.frames.length > 20) {
        // Trying to select a 20+ frame group
        if (newSelected.size > 0) {
          showToast(
            "Cannot select this size; only one size can be selected when it has more than 20 frames. Please deselect other sizes first.",
            5000,
          );
          return;
        }
        // Select first 20 frames from this 20+ frame group
        newSelected.add(dimension);
        const newFrameSelections = new Map(frameSelectionsPerDimension);
        newFrameSelections.set(
          dimension,
          new Set(Array.from({ length: 20 }, (_, i) => i)),
        );
        setFrameSelectionsPerDimension(newFrameSelections);
        setTotalSelectedFrames(20);
      } else {
        // Trying to select a <=20 frame group
        if (has20PlusGroupSelected) {
          showToast(
            "Cannot select this size when a larger size (20+ frames) is already selected. Please deselect the other size first.",
            5000,
          );
          return;
        }
        const newCount = totalSelectedFrames + group.frames.length;
        if (newCount > 20) {
          showToast(
            "This size cannot be selected since maximum 20 frames are allowed. Please unselect another size and select sizes adding up to a maximum of 20 frames.",
            5000,
          );
          return;
        }
        newSelected.add(dimension);
        setTotalSelectedFrames(newCount);
      }
    }

    setSelectedDimensions(newSelected);
  };

  const handleDimensionExpandToggle = (dimension: string) => {
    const newExpanded = new Set(expandedDimensions);
    if (newExpanded.has(dimension)) {
      newExpanded.delete(dimension);
    } else {
      newExpanded.add(dimension);
    }
    setExpandedDimensions(newExpanded);
  };

  const handleFrameToggle = (dimension: string, frameIndex: number) => {
    const frameIndices = frameSelectionsPerDimension.get(dimension);
    if (!frameIndices) return;

    const selectedIndices = Array.from(frameIndices).sort((a, b) => a - b);
    const minIndex = selectedIndices[0];
    const maxIndex = selectedIndices[selectedIndices.length - 1];

    const isAtBoundary =
      frameIndex === minIndex ||
      frameIndex === maxIndex ||
      frameIndex === minIndex - 1 ||
      frameIndex === maxIndex + 1;

    if (!isAtBoundary) {
      showToast(
        "Frames can only be selected or unselected from the top or end of the current selection.",
        3000,
      );
      return;
    }

    const newFrameIndices = new Set(frameIndices);
    const isCurrentlySelected = newFrameIndices.has(frameIndex);

    if (isCurrentlySelected) {
      // Unselect from boundary
      newFrameIndices.delete(frameIndex);
      setTotalSelectedFrames((prev) => prev - 1);
    } else {
      // Try to select at boundary
      if (totalSelectedFrames >= 20) {
        showToast(
          "Maximum 20 frames are allowed. Please unselect a frame first.",
          3000,
        );
        return;
      }
      newFrameIndices.add(frameIndex);
      setTotalSelectedFrames((prev) => prev + 1);
    }

    const newFrameSelections = new Map(frameSelectionsPerDimension);
    newFrameSelections.set(dimension, newFrameIndices);
    setFrameSelectionsPerDimension(newFrameSelections);
  };

  if (!previewData || previewData.length === 0) {
    return (
      <div className="preview-section">
        <div className="preview-area preview-area-filled">
          <div className="preview-images-container">
            <Label1 className="no-preview-text">
              No preview available for the uploaded storyboard.
            </Label1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="preview-section">
      <div className="preview-area preview-area-filled">
        <Label2 className="preview-image-count">
          {previewData.length} {previewData.length === 1 ? "frame" : "frames"}{" "}
          found
          {isAnimatedBanner && (
            <span className="selected-count">
              ({totalSelectedFrames} selected)
            </span>
          )}
        </Label2>

        {isAnimatedBanner ? (
          // Animated Banner: Dimension-based selection
          <div className="preview-images-container-animated">
            {dimensionGroups.map((group) => (
              <div key={group.dimension} className="dimension-section">
                <div className="dimension-header">
                  <div
                    className="dimension-checkbox-container"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDimensionToggle(group.dimension);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDimensions.has(group.dimension)}
                      onChange={() => {}} // Handled by parent div onClick
                      className="dimension-checkbox"
                    />
                  </div>
                  <div
                    className="dimension-info"
                    onClick={() => handleDimensionExpandToggle(group.dimension)}
                  >
                    <div className="dimension-text-wrapper">
                      <span className="dimension-name">{group.dimension}</span>
                      <span className="dimension-count">
                        ({group.frames.length}{" "}
                        {group.frames.length === 1 ? "frame" : "frames"}
                        {group.frames.length > 20 &&
                          selectedDimensions.has(group.dimension) && (
                            <span className="selected-frames-count">
                              ,{" "}
                              {frameSelectionsPerDimension.get(group.dimension)
                                ?.size || 0}{" "}
                              selected
                            </span>
                          )}
                        )
                      </span>
                    </div>
                    <Icon
                      type={
                        expandedDimensions.has(group.dimension)
                          ? "chevron-up"
                          : "chevron-down"
                      }
                      size="small"
                      className="dimension-expand-icon"
                    />
                  </div>
                </div>

                {expandedDimensions.has(group.dimension) && (
                  <div className="dimension-frames">
                    {group.frames.map((frame, frameIndex) => {
                      const imgUrl = (frame as any).imageUrl;
                      const isLargeGroup = group.frames.length > 20;
                      const isGroupSelected = selectedDimensions.has(
                        group.dimension,
                      );
                      const frameIndices =
                        frameSelectionsPerDimension.get(group.dimension) ||
                        new Set();
                      const isFrameSelected = frameIndices.has(frameIndex);
                      const selectedIndices = Array.from(frameIndices).sort(
                        (a, b) => a - b,
                      );
                      const minIndex = selectedIndices[0];
                      const maxIndex =
                        selectedIndices[selectedIndices.length - 1];
                      const canToggle =
                        isFrameSelected ||
                        frameIndex === minIndex - 1 ||
                        frameIndex === maxIndex + 1;

                      return (
                        <div
                          key={`storyboard-${frame.frame_id}`}
                          className={`preview-image-wrapper ${
                            isLargeGroup && isGroupSelected && !canToggle
                              ? "frame-disabled"
                              : ""
                          }`}
                        >
                          {isLargeGroup && isGroupSelected && (
                            <div className="frame-checkbox-wrapper">
                              <input
                                type="checkbox"
                                checked={isFrameSelected}
                                onChange={() =>
                                  handleFrameToggle(group.dimension, frameIndex)
                                }
                                disabled={!canToggle}
                                className="frame-checkbox"
                              />
                            </div>
                          )}
                          <div className="frame-content">
                            <Headline3 className="image-label">
                              Page {frame.page}
                            </Headline3>
                            {imgUrl ? (
                              <img
                                src={imgUrl}
                                alt={`Storyboard Frame ${frame.page}`}
                                className="storyboard-image-reveal"
                              />
                            ) : (
                              <div className="frame-placeholder">
                                Frame ID: {frame.frame_id}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Non-animated banner: Show all frames without selection
          <div className="preview-images-container">
            {previewData.map((frame) => {
              const imgUrl = (frame as any).imageUrl;
              return (
                <div
                  key={`storyboard-${frame.frame_id}`}
                  className="preview-image-wrapper"
                >
                  <Headline3 className="image-label">
                    Page {frame.page}
                  </Headline3>
                  {frame.dimension && (
                    <Label2 className="image-metadata">
                      {frame.dimension || "Size info pending"}
                    </Label2>
                  )}
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt={`Storyboard Frame ${frame.page}`}
                      className="storyboard-image-reveal"
                    />
                  ) : (
                    <div className="frame-placeholder">
                      Frame ID: {frame.frame_id}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryboardPreview;
